/**
 * EditarCobroDialog (2026-08-31, dirección — URGENTE): corregir un cobro desde
 * la ficha del paciente. Cualquier miembro del equipo puede editar concepto,
 * importe, método, fecha e IVA; el backend recalcula totales y la
 * trazabilidad apunta quién cambió qué (valor anterior → nuevo).
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Check } from 'lucide-react';
import { api, formatEUR } from '../lib/api';
import { PAYMENT_METHODS } from '../lib/cobroQuickHelpers';
import { isDevolucionConcept } from '../lib/cobroPresets';
import { desgloseGuardado, desgloseIvaIncluido, esIvaIncluido } from '../lib/iva';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

export default function EditarCobroDialog({ cobro, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!cobro) { setForm(null); return; }
    // Una devolución (a mano o automática) se enseña y se corrige en
    // positivo; se guarda en negativo (dirección, 2026-09-08).
    const importe = cobro.amount ?? cobro.amount_base ?? '';
    const devolucion = cobro.source === 'devolucion' || isDevolucionConcept(cobro.concept);
    setForm({
      concept: cobro.concept || '',
      amount: importe === '' ? '' : String(devolucion ? Math.abs(Number(importe)) : importe),
      // Sin "Tarjeta" silenciosa (2026-08-31): si el cobro no trae método,
      // se deja vacío y hay que elegirlo antes de guardar.
      payment_method: cobro.payment_method || '',
      date: (cobro.date || '').slice(0, 10),
      vat_applied: !!cobro.vat_applied,
    });
  }, [cobro]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const esDevolucion = !!form && (cobro?.source === 'devolucion' || isDevolucionConcept(form.concept));
  // Cada cobro se corrige con la cuenta con la que se apuntó (24/9/2026).
  // Desde ese día el importe es lo que paga el paciente, con el IVA DENTRO;
  // los de antes llevan el 21% ENCIMA y así siguen: el servidor los corrige
  // con su cuenta de entonces y aquí se dice, en vez de enseñar un desglose
  // que no es el suyo.
  const ivaIncluido = esIvaIncluido(cobro);
  const importeEscrito = form ? parseFloat(String(form.amount).replace(',', '.')) : NaN;
  const desglose = ivaIncluido && form && !esDevolucion
    ? (cobro?.lines?.length > 1
      ? desgloseIvaIncluido(desgloseGuardado(cobro).total, form.vat_applied)
      : desgloseIvaIncluido(Number.isNaN(importeEscrito) ? 0 : importeEscrito, form.vat_applied))
    : null;

  const guardar = async () => {
    const importe = parseFloat(String(form.amount).replace(',', '.'));
    if (Number.isNaN(importe) || importe < 0) { toast.error('Importe no válido'); return; }
    if (!form.concept.trim()) { toast.error('El concepto no puede quedar vacío'); return; }
    if (!form.payment_method) { toast.error('Elige el método de pago'); return; }
    setGuardando(true);
    try {
      const desglosado = cobro?.lines?.length > 1;
      await api.put(`/api/cobros/${cobro.id}`, {
        concept: form.concept.trim(),
        ...(desglosado ? {} : { amount: esDevolucion ? -importe : importe, payment_method: form.payment_method }),
        date: form.date || undefined,
        vat_applied: esDevolucion ? false : form.vat_applied,
      });
      toast.success(esDevolucion ? 'Devolución corregida' : 'Cobro corregido');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar el cobro');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={!!cobro} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Editar cobro</DialogTitle>
          {cobro?.created_by_name && (
            <p className="text-xs text-muted-foreground">
              Registrado por <strong>{cobro.created_by_name}</strong>
              {cobro.created_at ? ` · ${cobro.created_at.slice(0, 10)}` : ''} — tus cambios quedarán registrados a tu nombre.
            </p>
          )}
        </DialogHeader>
        {form && cobro?.lines?.length > 1 && (
          <p className="text-xs rounded bg-amber-50 text-amber-900 border border-amber-200 px-2 py-1.5">
            Este cobro está desglosado en {cobro.lines.length} pagos: el importe y el método
            se corrigen desde la página Cobros para no descuadrar las líneas.
          </p>
        )}
        {form && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="ec-concepto">Concepto</Label>
              <Input id="ec-concepto" value={form.concept} onChange={(e) => set('concept', e.target.value)}
                className="mt-1" data-testid="editar-cobro-concepto" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ec-importe">{esDevolucion ? 'Importe devuelto (€)' : (ivaIncluido ? 'Importe que paga (€)' : 'Importe (€, sin IVA)')}</Label>
                <Input id="ec-importe" inputMode="decimal" value={form.amount}
                  onChange={(e) => set('amount', e.target.value)} className="mt-1 tabular-nums"
                  disabled={cobro?.lines?.length > 1}
                  data-testid="editar-cobro-importe" />
              </div>
              <div>
                <Label htmlFor="ec-fecha">Fecha</Label>
                <Input id="ec-fecha" type="date" value={form.date}
                  onChange={(e) => set('date', e.target.value)} className="mt-1"
                  data-testid="editar-cobro-fecha" />
              </div>
            </div>
            <div>
              <Label>Método de pago</Label>
              <Select value={form.payment_method} onValueChange={(v) => set('payment_method', v)} disabled={cobro?.lines?.length > 1}>
                <SelectTrigger className="mt-1" data-testid="editar-cobro-metodo">
                  <SelectValue placeholder="— Elige el método —" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {esDevolucion ? (
              <p className="text-xs rounded bg-amber-50 text-amber-900 border border-amber-200 px-2 py-1.5" data-testid="editar-cobro-devolucion">
                Devolución: el importe se guarda en negativo (resta de la caja) y sin IVA.
              </p>
            ) : (
              <>
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input type="checkbox" checked={form.vat_applied}
                    onChange={(e) => set('vat_applied', e.target.checked)}
                    className="h-4 w-4 accent-[#2E6E5E]" data-testid="editar-cobro-iva" />
                  {ivaIncluido ? 'Con IVA 21% (incluido en el importe)' : 'Con IVA (21%) — el total se recalcula solo'}
                </label>
                {desglose && form.vat_applied && (
                  <p className="text-xs text-muted-foreground tabular-nums" data-testid="editar-cobro-desglose">
                    Base imponible {formatEUR(desglose.base)} · IVA (21%) {formatEUR(desglose.iva)} · Total {formatEUR(desglose.total)}
                  </p>
                )}
                {!ivaIncluido && (form.vat_applied || cobro?.vat_applied) && (
                  <p className="text-xs rounded bg-slate-50 text-slate-700 border border-slate-200 px-2 py-1.5" data-testid="editar-cobro-cuenta-antigua">
                    Cobro apuntado antes del 24/9/2026: su importe es la base y el 21% va encima,
                    como se calculó entonces. Se corrige con esa misma cuenta.
                  </p>
                )}
              </>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando || !form}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="editar-cobro-guardar">
            {guardando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
